import { Fragment, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Modal } from "./ui/Modal";
import { buttonClass } from "./ui/styles";

const CROP_TYPES = {
  BACKGROUND: "background",
  LOGO: "logo",
  STAMP: "stamp",
  MILESTONE: "milestone",
};

const ASPECT_RATIOS = {
  [CROP_TYPES.BACKGROUND]: 1125 / 432,
  [CROP_TYPES.LOGO]: 1,
  [CROP_TYPES.STAMP]: 1,
  [CROP_TYPES.MILESTONE]: 1,
};

// Output size in CSS pixels (the canvas is that times devicePixelRatio).
const OUTPUT_SIZES = {
  [CROP_TYPES.BACKGROUND]: { width: 1140, height: 435 },
  [CROP_TYPES.LOGO]: { width: 200, height: 200 },
  [CROP_TYPES.STAMP]: { width: 200, height: 200 },
  [CROP_TYPES.MILESTONE]: { width: 200, height: 200 },
};

// Draws the percent crop of `image` onto a canvas of `size` CSS pixels and
// resolves to a PNG Blob; rejects when the canvas yields nothing.
function cropToBlob(image, crop, size) {
  const canvas = document.createElement("canvas");
  const pixelRatio = window.devicePixelRatio || 1;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const pixelCrop = {
    x: (crop.x * image.width) / 100,
    y: (crop.y * image.height) / 100,
    width: (crop.width * image.width) / 100,
    height: (crop.height * image.height) / 100,
  };
  // "crop": as large as the crop box is on screen (the store logo's size).
  const target =
    size === "crop"
      ? { width: pixelCrop.width, height: pixelCrop.height }
      : size;

  canvas.width = target.width * pixelRatio;
  canvas.height = target.height * pixelRatio;

  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("No 2d context"));

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    target.width,
    target.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas is empty"))),
      "image/png",
      1,
    );
  });
}

// Crops one image to the aspect of `type`. Defaults are what the pass editor
// relies on; the optional props serve the redesigned Settings logo upload:
//   - variant: "classic" (default) | "redesign" — the redesign's Modal and
//     buttons, a description line, and a checkerboard behind the image.
//   - output: "url" (default, onCropComplete gets an object URL) | "blob"
//     (onCropComplete gets the PNG Blob itself, named `outputFileName`).
//   - outputSize: { width, height } in CSS px, or "crop" for the crop box's
//     on-screen size. Default: by type (1140×435 background, 200×200 others).
// After onCropComplete it calls onClose; a failed crop is logged and leaves
// the dialog open.
export default function ImageCropModal({
  isOpen,
  onClose,
  imageUrl,
  onCropComplete,
  type = CROP_TYPES.BACKGROUND,
  title,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  variant = "classic",
  description,
  output = "url",
  outputSize,
  outputFileName,
}) {
  const [crop, setCrop] = useState();
  const [imageRef, setImageRef] = useState(null);

  const onImageLoad = useCallback(
    (e) => {
      if (!e?.target?.width) {
        return;
      }
      const { width, height } = e.target;
      setImageRef(e.target);

      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 100,
          },
          ASPECT_RATIOS[type],
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(crop);
    },
    [type],
  );

  const handleCropComplete = useCallback(async () => {
    if (imageRef && crop?.width && crop?.height) {
      try {
        const blob = await cropToBlob(
          imageRef,
          crop,
          outputSize ||
            OUTPUT_SIZES[type] ||
            OUTPUT_SIZES[CROP_TYPES.BACKGROUND],
        );
        if (output === "blob") {
          if (outputFileName) blob.name = outputFileName;
          onCropComplete(blob);
        } else {
          onCropComplete(URL.createObjectURL(blob));
        }
        onClose();
      } catch (error) {
        console.error("Cropping failed:", error);
      }
    }
  }, [
    imageRef,
    crop,
    type,
    output,
    outputSize,
    outputFileName,
    onCropComplete,
    onClose,
  ]);

  const circular = type === CROP_TYPES.STAMP || type === CROP_TYPES.MILESTONE;

  if (variant === "redesign") {
    return (
      <Modal
        open={!!isOpen}
        onClose={onClose}
        title={title}
        description={description}
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              className={buttonClass.secondary}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleCropComplete}
              disabled={!imageRef}
              className={buttonClass.primary}
            >
              {saveLabel}
            </button>
          </>
        }
      >
        <div className="flex justify-center rounded-xl bg-[repeating-conic-gradient(#F3F4F6_0%_25%,#FFFFFF_0%_50%)] bg-[length:20px_20px] p-2">
          {imageUrl && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={ASPECT_RATIOS[type]}
              minWidth={100}
              circularCrop={circular}
            >
              <img
                src={imageUrl}
                onLoad={onImageLoad}
                alt=""
                style={{ maxWidth: "100%", maxHeight: 360 }}
                className={circular ? "rounded-none" : undefined}
              />
            </ReactCrop>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 mb-4"
                >
                  {title}
                </Dialog.Title>

                <div className="mt-2">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    aspect={ASPECT_RATIOS[type]}
                    minWidth={100}
                    circularCrop={circular}
                  >
                    <img
                      src={imageUrl}
                      onLoad={onImageLoad}
                      alt="Crop preview"
                      style={{ maxWidth: "100%" }}
                      className={circular ? "rounded-none" : undefined}
                    />
                  </ReactCrop>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                    onClick={handleCropComplete}
                  >
                    {saveLabel}
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={onClose}
                  >
                    {cancelLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Export constants for external use
export { CROP_TYPES };
